import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode, MeshCurve, getTemplateNodeReturns, skipped, EMPTY_LIST, TypeDescriptors, visitNone} from "@cm/template-nodes"
import type {
    UVControlPoint as MeshCurveUVControlPoint,
    OnFilterActiveContext,
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {mattressCurveLikeMarker} from "#mattress/role-markers"
import {mattressCurveLike, mattressReferenceFrameLike} from "#mattress/validation"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import {MattresCurveUVChannel, getMattressCurveLocalNode} from "#mattress/nodes/curve/mattress-curve"
import type {MattressCurveControlPointData, MattressCurveData} from "#mattress/nodes/curve/mattress-curve"
import type {MattressCurveLocalNodesData, MattressNodePath} from "#mattress/nodes/component/mattress-component-types"
import {getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"

const TD = TypeDescriptors

const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

export type MattressComputedCurveSwapReferenceFrameParameters = {
    type: "MattressComputedCurveSwapReferenceFrame"
    curve: MattressCurveLikeNode | null
    oldReferenceFrame: MattressReferenceFrameLikeNode | null
    newReferenceFrame: MattressReferenceFrameLikeNode | null
}

export type MattressComputedCurveParameters = {
    name: string
    parameters: MattressComputedCurveSwapReferenceFrameParameters
}

const swapReferenceFrameParameters = z.object({
    type: z.literal("MattressComputedCurveSwapReferenceFrame"),
    curve: z.nullable(mattressCurveLike),
    oldReferenceFrame: z.nullable(mattressReferenceFrameLike),
    newReferenceFrame: z.nullable(mattressReferenceFrameLike),
})

const definition: TemplateNodeDefinition<MattressComputedCurveParameters> = {
    nodeClass: "MattressComputedCurve",
    validation: {
        parameters: z.object({
            name: z.string(),
            parameters: swapReferenceFrameParameters,
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressComputedCurve extends MattressNode<MattressComputedCurveParameters> implements MattressCurveLikeNode {
    get [mattressCurveLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressComputedCurveParameters> {
        const {type} = this.parameters.parameters

        if (type === "MattressComputedCurveSwapReferenceFrame") {
            const {curve, oldReferenceFrame, newReferenceFrame} = this.parameters.parameters
            if (curve === null || oldReferenceFrame === null || newReferenceFrame === null) return skipped
            return visitNone(this.parameters)
        }

        return skipped
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressComputedCurveParameters> {
        const {type} = this.parameters.parameters
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {templateScope, templateContext} = evaluator
        const {sceneManager} = templateContext
        const {evaluatedData, evaluatePartialMattressMeshData, nodePath} = mattressContext
        const {localNodesDataLists} = evaluatedData

        const scope = evaluator.getScope(this)

        const localMeshCurveSrc = scope.const(
            () =>
                new MeshCurve({
                    parameters: {
                        name: this.parameters.name,
                        resolution: undefined,
                        closed: false,
                        controlPoints: [] as MeshCurveUVControlPoint[],
                        mesh: null,
                        uvChannel: MattresCurveUVChannel,
                        visible: true,
                    },
                }),
            "localMeshCurveSrc",
        )

        if (type === "MattressComputedCurveSwapReferenceFrame") {
            if (
                this.parameters.parameters.curve === null ||
                this.parameters.parameters.oldReferenceFrame === null ||
                this.parameters.parameters.newReferenceFrame === null
            )
                return skipped

            const [sourceCurveData] = scope.branch(evaluator.evaluate(this.parameters.parameters.curve))
            const [oldReferenceFrameData] = scope.branch(evaluator.evaluate(this.parameters.parameters.oldReferenceFrame))
            const [newReferenceFrameData] = scope.branch(evaluator.evaluate(this.parameters.parameters.newReferenceFrame))

            const defferedSourceCurveData = scope.waitForFinishedModifyingScene(sourceCurveData, sceneManager)
            const defferedOldReferenceFrameData = scope.waitForFinishedModifyingScene(oldReferenceFrameData, sceneManager)
            const defferedNewReferenceFrameData = scope.waitForFinishedModifyingScene(newReferenceFrameData, sceneManager)

            const sourceControlPointsData = scope.get(defferedSourceCurveData, "controlPoints")
            const closed = scope.get(defferedSourceCurveData, "closed")
            const resolution = scope.get(defferedSourceCurveData, "resolution")

            const newControlPointsData = scope.pureLambda(
                scope.tuple(sourceControlPointsData, defferedOldReferenceFrameData, defferedNewReferenceFrameData),
                ([sourceControlPointsData, oldReferenceFrameData, newReferenceFrameData]) => {
                    const newControlPointsData: MattressCurveControlPointData[] = []
                    for (const controlPointData of sourceControlPointsData) {
                        newControlPointsData.push({
                            ...controlPointData,
                            uvSpace:
                                controlPointData.uvSpace !== "global" && controlPointData.uvSpace.contentHash === oldReferenceFrameData.contentHash
                                    ? newReferenceFrameData
                                    : controlPointData.uvSpace,
                        })
                    }

                    return newControlPointsData
                },
                "newControlPoints",
            )

            const [mattressMeshDataValid] = scope.branch(evaluatePartialMattressMeshData(scope, evaluator))
            const [baseMeshLocalNodesValid] = scope.branch(scope.get(mattressMeshDataValid, "baseMeshLocalNodes"))
            const [baseMeshLocalNodeValid] = scope.branch(scope.get(baseMeshLocalNodesValid, "mesh"))

            const newLocalNode = getMattressCurveLocalNode(scope, localMeshCurveSrc, resolution, closed, newControlPointsData, baseMeshLocalNodeValid)

            const newNodePath = scope.pureLambda(
                scope.tuple(nodePath, this),
                ([nodePath, instance]) => {
                    return [...nodePath, instance]
                },
                "newNodePath",
                {
                    output: mattressNodePathArrayDesc,
                },
            )

            const localNodeData = scope.struct<MattressCurveLocalNodesData>("localNodeData", {
                type: "mattressCurve",
                nodes: scope.list([newLocalNode]),
                path: newNodePath,
            })
            const localNodesDataList = scope.phi(
                scope.pureLambda(localNodeData, (localNodeData) => (localNodeData ? [localNodeData] : EMPTY_LIST), "localNodesDataList"),
                EMPTY_LIST,
            )
            localNodesDataLists.push(localNodesDataList)

            const curveData = scope.struct<MattressCurveData>("curveData", {
                localNode: newLocalNode,
                controlPoints: newControlPointsData,
                path: newNodePath,
                closed,
                resolution,
            })

            const localId = evaluator.getLocalId(this)
            templateScope.alias(curveData, `mattressCurveData-${localId}`)
        } else throw new Error(`Unsupported type: ${type} in mattress computed curve node`)

        return visitNone(this.parameters)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<MattressCurveData | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<MattressCurveData>(`mattressCurveData-${evaluator.getLocalId(this)}`))
    }
}
